/**
 * calculations.ts — VIREON Cortex Core Analytics
 * ================================================
 * Pure numeric utilities, anomaly detection, KPI builders, and domain
 * helpers. All functions operate on TelemetryRow arrays already enriched
 * by derivedEngine.ts.
 *
 * No tariff logic lives here — that belongs in derivedEngine / billing.
 */

import type { TelemetryRow, KpiSummary } from '@/types';
import {
  DEFAULT_THRESHOLDS,
  MAX_INTEGRATION_GAP_H,
  RISK_META,
  type RiskLevel,
  type ToDPeriod,
} from '@/config/constants';

/* -------------------------------------------------------------------------- */
/*  Small numeric utilities                                                   */
/* -------------------------------------------------------------------------- */

export const safe = (n: number, fallback = 0): number =>
  Number.isFinite(n) ? n : fallback;

export const sum  = (xs: number[]): number => xs.reduce((a, b) => a + safe(b), 0);
export const mean = (xs: number[]): number => (xs.length ? sum(xs) / xs.length : 0);
export const min  = (xs: number[]): number =>
  xs.length ? Math.min(...xs.map((x) => safe(x, Infinity))) : 0;
export const max  = (xs: number[]): number =>
  xs.length ? Math.max(...xs.map((x) => safe(x, -Infinity))) : 0;

/* -------------------------------------------------------------------------- */
/*  Outlier utilities                                                         */
/* -------------------------------------------------------------------------- */

/** Returns the value at percentile p (0–100) of a number array. */
export function percentile(values: number[], p: number): number {
  const clean = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (!clean.length) return 0;
  const idx = (p / 100) * (clean.length - 1);
  const lo  = Math.floor(idx);
  const hi  = Math.ceil(idx);
  if (lo === hi) return clean[lo];
  return clean[lo] + (clean[hi] - clean[lo]) * (idx - lo);
}

/** Clamps array values to the 99.5th-percentile ceiling (or drops if exclude:true). */
export function clampOutliers(
  values: number[],
  opts: { p?: number; exclude?: boolean } = {},
): number[] {
  const { p = 99.5, exclude = false } = opts;
  const ceiling = percentile(values, p);
  if (ceiling <= 0) return values;
  return exclude
    ? values.filter((v) => v <= ceiling)
    : values.map((v) => (v > ceiling ? ceiling : v));
}

/** Returns [0.5th, 99.5th] percentile band for sensor dropout / spike detection. */
export function computeBounds(values: number[]): { floor: number; ceiling: number } {
  return {
    floor:   percentile(values, 0.5),
    ceiling: percentile(values, 99.5),
  };
}

/* -------------------------------------------------------------------------- */
/*  Risk level utilities                                                      */
/* -------------------------------------------------------------------------- */

const RISK_RANK: Record<RiskLevel, number> = {
  NORMAL: 0, WARNING: 1, HIGH: 2, CRITICAL: 3,
};

export function maxRisk(levels: RiskLevel[]): RiskLevel {
  let highest: RiskLevel = 'NORMAL';
  for (const l of levels) {
    if (RISK_RANK[l] > RISK_RANK[highest]) highest = l;
  }
  return highest;
}

export const riskMeta = (l: RiskLevel) => RISK_META[l];

/* -------------------------------------------------------------------------- */
/*  Power Factor helpers                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Resolved power factor for a row. Uses the derived pfAvg (already cleaned
 * by derivedEngine); falls back to kW/kVA if zero.
 */
export function powerFactor(row: TelemetryRow): number {
  const pf = Math.abs(safe(row.pfAvg));
  if (pf > 0 && pf <= 1.0001) return Math.min(pf, 1);
  const kva = safe(row.kvaTotal);
  return kva > 0 ? Math.min(safe(row.kwTotal) / kva, 1) : 0;
}

/**
 * True when a row has a trustworthy PF reading (iTotal ≥ 1A, pfAvg > 0).
 * Mirrors derivedEngine.pfValid; recomputed here for use in analytics filters.
 */
export function isValidPfReading(row: TelemetryRow): boolean {
  const pf = Math.abs(safe(row.pfAvg));
  if (!(pf > 0)) return false;
  // currentTotal = iTotal alias set by derivedEngine / sheetsClient
  const i = safe(row.currentTotal ?? row.iTotal);
  return i > 1.0;
}

/* -------------------------------------------------------------------------- */
/*  Domain energy helpers                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Capacity factor: average kW / peak kW × 100.
 */
export function capacityFactor(rows: TelemetryRow[]): number {
  if (!rows.length) return 0;
  const avg  = mean(rows.map((r) => safe(r.kwTotal)));
  const peak = max(rows.map((r) => safe(r.kwTotal)));
  return peak > 0 ? (avg / peak) * 100 : 0;
}

/**
 * Load factor: energy / (peak demand × hours) × 100.
 *
 * WB Spec AY denominator: 1000 × Max_demand_kW_current × time(curr. hours)
 * Max_demand_kW_current = the billing-cycle 15-minute maximum demand in kW.
 *
 * BUG FIX (WB): previous implementation used max(r.kwTotal) — the largest
 * instantaneous Watts_Total reading. With 5-min poll intervals a single
 * high-load reading inside a 15-min block inflates peakKw, causing LF to be
 * understated. This can push the consumer into a higher LF surcharge band or
 * out of a rebate band entirely.
 *
 * Correct approach: derive 15-min max kW as maxDemandKvaOf(rows) × weightedPF,
 * matching derivedEngine.ts which uses its closed 15-min window maxDemandKw.
 * Falls back to instantaneous max kwTotal when 15-min blocks are unavailable
 * (< 2 rows) or PF cannot be resolved.
 *
 * Telangana path: loadFactor is not used for billing (no LF rebate), so
 * the improvement has no effect on TGSPDCL calculations.
 */
export function loadFactor(rows: TelemetryRow[]): number {
  if (rows.length < 2) return 0;
  const energyKwh = energyDelivered(rows);
  // ── 15-min max kW (spec AY denominator) ─────────────────────────────────
  // Step 1: get 15-min averaged max kVA from demand blocks.
  const maxKva  = maxDemandKvaOf(rows);
  // Step 2: convert to kW via weighted PF (ΔWh/ΔVAh register ratio).
  const sorted  = [...rows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const dWh     = Math.max(0, safe(sorted[sorted.length-1].energyWh)  - safe(sorted[0].energyWh))  / 1000;
  const dVAh    = Math.max(0, safe(sorted[sorted.length-1].energyVAh) - safe(sorted[0].energyVAh));
  const pfWtd   = dVAh > 0 ? Math.min(dWh / dVAh, 1) : 0;
  const peakKw  = maxKva > 0 && pfWtd > 0
    ? maxKva * pfWtd                                          // 15-min max kW per spec
    : max(rows.map((r) => safe(r.kwTotal)));                  // fallback: instantaneous max
  const hours   = (sorted[sorted.length-1].timestamp.getTime() - sorted[0].timestamp.getTime()) / 3_600_000;
  if (peakKw <= 0 || hours <= 0) return 0;
  return Math.min((energyKwh / (peakKw * hours)) * 100, 100);
}

/**
 * Energy delivered between first and last sample of a window.
 * Uses trapezoidal integration of the raw Watts_Total samples so the result
 * reflects the actual sampled curve for the selected window.
 */
export function energyDelivered(rows: TelemetryRow[]): number {
  if (rows.length < 2) return 0;

  let trapTotal = 0;
  for (let i = 1; i < rows.length; i++) {
    const dtH = (rows[i].timestamp.getTime() - rows[i - 1].timestamp.getTime()) / 3_600_000;
    // Skip gaps longer than MAX_INTEGRATION_GAP_H. Two adjacent samples that
    // are hours apart are NOT a real continuous load — this happens whenever a
    // window is filtered into non-contiguous slices (e.g. OFF-PEAK = 23:00–06:00
    // leaves a ~17h hole between the morning block and the late-night block) or
    // when the meter drops out. Bridging that hole with a single trapezoid
    // invents energy that never flowed. costRupees() already guards this way.
    if (dtH <= 0 || dtH > MAX_INTEGRATION_GAP_H) continue;
    const wattsLeft  = safe(rows[i - 1].wattsTotal);
    const wattsRight = safe(rows[i].wattsTotal);
    trapTotal += ((wattsLeft + wattsRight) / 2) * dtH;
  }

  return trapTotal / 1000;
}

/**
 * Apparent energy (kVAh) for a window.
 *
 * PRIMARY METHOD — VAh register delta (energyVAh = VAh_Received from meter):
 *   kVAh = (VAh_Received[last] − VAh_Received[first]) / 1000
 *
 * This mirrors how energyDelivered() uses the Wh register, and is the only
 * method consistent with the TGSPDCL billing PF formula
 *   PF = ΣWh_received / ΣVAh_received  (PDF §AN).
 *
 * WHY NOT Watts/True_PF_Avg (the previous approach):
 *   The L&T LK4405 reports True_PF_Avg — the true (harmonic-inclusive) PF,
 *   typically 1.5–2% below the billing-PF ratio ΔWh/ΔVAh at this site.
 *   Using Watts / True_PF_Avg as instantaneous VA therefore OVERESTIMATES
 *   apparent power by the same ~1.5%, inflating daily kVAh by ~275 kVAh
 *   and understating PF by ~0.015, producing incorrect billing estimates of
 *   ~Rs 1,970/day (Rs 59,000/month) above the meter's own register.
 *
 * FALLBACK — trapezoidal integration of VA_Total (the meter's own apparent-
 *   power register, NOT Watts/PF): used only when the VAh register is
 *   unavailable (energyVAh = 0 or undefined for all rows).
 *   Plausibility guard: delta > 10 MVAh per call is rejected as a register
 *   reset artifact (mirrors derivedEngine.ts MAX_PLAUSIBLE_DELTA guard).
 */
export function apparentEnergyKvah(rows: TelemetryRow[]): number {
  if (rows.length < 2) return 0;

  const sorted = [...rows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  const first  = sorted[0];
  const last   = sorted[sorted.length - 1];

  // ── Primary: VAh register delta ──────────────────────────────────────────
  const vahFirst = safe(first.energyVAh);
  const vahLast  = safe(last.energyVAh);
  const delta    = vahLast - vahFirst;

  // Accept positive deltas up to 10 MVAh (10,000,000 VAh) per call.
  // A negative or zero delta means the register isn't populated; a delta
  // above 10 MVAh signals a reset spike — both fall through to the fallback.
  if (delta > 0 && delta < 10_000_000) return delta / 1000; // VAh → kVAh

  // ── Fallback: trapezoidal integration of VA_Total ────────────────────────
  // VA_Total is the meter's own apparent-power register — no PF arithmetic.
  let total = 0;
  for (let i = 1; i < sorted.length; i++) {
    const dtH = (sorted[i].timestamp.getTime() - sorted[i - 1].timestamp.getTime()) / 3_600_000;
    if (dtH <= 0 || dtH > MAX_INTEGRATION_GAP_H) continue;
    total += ((safe(sorted[i - 1].vaTotal) + safe(sorted[i].vaTotal)) / 2) * dtH;
  }
  return total / 1000;
}

/**
 * Approximate energy cost for a window using the per-row derived
 * effectiveRate (bakes in PF rebate adjustment).
 * Used for WBSEDCL (kWh billing). For TGSPDCL, Dashboard.tsx uses
 * buildBillBreakdown directly so Power and Billing pages are always identical.
 */
export function costRupees(rows: TelemetryRow[]): number {
  if (rows.length < 2) return 0;
  let cost = 0;
  for (let i = 1; i < rows.length; i++) {
    const dtH  = (rows[i].timestamp.getTime() - rows[i - 1].timestamp.getTime()) / 3_600_000;
    if (dtH <= 0 || dtH > 1) continue;
    const kw   = (safe(rows[i - 1].kwTotal) + safe(rows[i].kwTotal)) / 2;
    const rate = safe(rows[i].effectiveRate ?? rows[i].rateRsKwh);
    cost += kw * dtH * rate;
  }
  return cost;
}

/**
 * Voltage phase imbalance % (NEMA method, also used in PDF §2.3 L).
 * max |Vphase − Vavg| / Vavg × 100
 */
export function phaseImbalancePct(row: TelemetryRow): number {
  const phases = [row.vR, row.vY, row.vB].filter(Number.isFinite) as number[];
  if (phases.length < 3) return safe(row.voltageUnbalancePct);
  const avg = mean(phases);
  if (avg <= 0) return 0;
  const dev = max(phases.map((v) => Math.abs(v - avg)));
  return (dev / avg) * 100;
}

/**
 * Latest-snapshot KPIs for the header strip.
 * For TGSPDCL (kVAh), Dashboard.tsx overrides cost/energy fields using
 * buildBillBreakdown — ensuring Power page = Billing page exactly.
 * This function's cost output is the canonical source for WBSEDCL only.
 */
export function buildKpiSummary(rows: TelemetryRow[]): KpiSummary {
  const empty: KpiSummary = {
    kwhToday: 0, kvahToday: 0, pfNow: 0, costToday: 0,
    kwhYesterday: 0, kvahYesterday: 0, pfYesterday: 0, costYesterday: 0,
    kwhMtd: 0, kvahMtd: 0, pfMtd: 0, costMtd: 0,
    riskNow: 'NORMAL', freshSeconds: Number.POSITIVE_INFINITY,
  };
  if (!rows.length) return empty;

  const latest    = rows[rows.length - 1];
  // Anchor "now" to the LAST DATA ROW's timestamp, not the wall clock.
  // The sheet's latest reading is rarely the exact wall-clock day, so a
  // wall-clock "today/yesterday/this-week" window finds no rows and returns 0
  // while month-to-date (which spans from the 1st) still shows data — exactly
  // the "today=0, yesterday=0, month works" symptom. Data-anchoring fixes it.
  const now       = latest.timestamp;
  const y  = now.getFullYear(), mo = now.getMonth(), d = now.getDate();

  // Date strings (YYYY-MM-DD) built from LOCAL parts — never toISOString(),
  // which converts to UTC and shifts the date back a day in IST (UTC+5:30).
  const todayDate     = latest.date;
  const yd            = new Date(y, mo, d - 1);
  const yesterdayDate = `${yd.getFullYear()}-${String(yd.getMonth() + 1).padStart(2, '0')}-${String(yd.getDate()).padStart(2, '0')}`;

  const todayRows     = rows.filter((r) => r.date === todayDate);
  const yesterdayRows = rows.filter((r) => r.date === yesterdayDate);
  const mtdStart      = new Date(y, mo, 1, 0, 0, 0, 0).getTime();
  const mtdRows       = rows.filter((r) => r.timestamp.getTime() >= mtdStart);

  // ── Day-boundary anchor rows ──────────────────────────────────────────────
  // energyDelivered() and apparentEnergyKvah() both use the first row in the
  // supplied array as the left edge of the first trapezoidal interval (for the
  // trap fallback path) and as the register-start reference (for the primary
  // register-delta path). Without an anchor row the first ~5-minute polling
  // interval of the day is silently dropped, understating kWh by ~0.02% and
  // kVAh by a similar margin.
  //
  // Fix: prepend the last row of the PRECEDING day so that:
  //   • register-delta path: delta = last_of_day.VAh - last_of_prev_day.VAh
  //     (exactly matches the meter's accumulated energy for the full calendar day)
  //   • trap fallback path: the first interval from prev-day-last to first-of-day
  //     is included, closing the midnight gap.
  //
  // The anchor rows are sorted-in automatically because both helpers sort
  // their input before processing.

  // Helper: last row strictly before a given date string
  const lastRowBefore = (dateStr: string): TelemetryRow | undefined =>
    [...rows]
      .filter((r) => r.date < dateStr)
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())[0];

  const todayAnchor     = lastRowBefore(todayDate);
  const yesterdayAnchor = lastRowBefore(yesterdayDate);

  const todayRowsAnchored     = todayAnchor     ? [todayAnchor,     ...todayRows]     : todayRows;
  const yesterdayRowsAnchored = yesterdayAnchor ? [yesterdayAnchor, ...yesterdayRows] : yesterdayRows;

  // BUG FIX (Bug #3 — PF method): PDF §AN defines Power Factor as
  //   PF = ΣWh_received / ΣVAh_received (ratio of cumulative register deltas).
  // The previous implementation averaged per-row |pfAvg| readings, which are
  // noisier than the register ratio and systematically read ~1.6% lower at
  // this site (0.981 vs 0.9968), incorrectly triggering "Near limit" warnings
  // and producing billing-PF values inconsistent with the Billings page.
  //
  // registerPF() uses the sorted window's first and last rows to compute the
  // delta, matching the weightedPF() already used in engines/telangana.ts and
  // billing.ts. It falls back to the instantaneous mean only when no register
  // data is available (edge case: single-row windows).
  const registerPF = (rs: TelemetryRow[]): number => {
    if (rs.length < 2) {
      // Single-row fallback: use instantaneous pfAvg
      const valid = rs.filter(isValidPfReading);
      if (!valid.length) return 0;
      return mean(valid.map((r) => powerFactor(r)).filter((v) => v > 0));
    }
    const sorted  = [...rs].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    const first   = sorted[0];
    const last    = sorted[sorted.length - 1];
    const dWh     = Math.max(0, safe(last.energyWh)  - safe(first.energyWh));
    const dVAh    = Math.max(0, safe(last.energyVAh) - safe(first.energyVAh));
    if (dVAh > 0) return Math.min(dWh / dVAh, 0.9999);
    // Register data unavailable — fall back to instantaneous mean
    const valid = rs.filter(isValidPfReading);
    if (!valid.length) return 0;
    return mean(valid.map((r) => powerFactor(r)).filter((v) => v > 0));
  };

  return {
    kwhToday:      energyDelivered(todayRowsAnchored),
    kvahToday:     apparentEnergyKvah(todayRowsAnchored),
    pfNow:         powerFactor(latest),
    costToday:     costRupees(todayRows),

    kwhYesterday:  energyDelivered(yesterdayRowsAnchored),
    kvahYesterday: apparentEnergyKvah(yesterdayRowsAnchored),
    pfYesterday:   registerPF(yesterdayRowsAnchored),
    costYesterday: costRupees(yesterdayRows),

    kwhMtd:        energyDelivered(mtdRows),
    kvahMtd:       apparentEnergyKvah(mtdRows),
    pfMtd:         registerPF(mtdRows),
    costMtd:       costRupees(mtdRows),

    riskNow:       latest.fireRiskLevel,
    freshSeconds:  Math.max(0, (Date.now() - latest.timestamp.getTime()) / 1000),
  };
}

/* -------------------------------------------------------------------------- */
/*  Anomaly detection                                                         */
/* -------------------------------------------------------------------------- */

export interface AnomalyHit {
  kind:
    | 'neutralCurrent'
    | 'voltageUnbalance'
    | 'currentUnbalance'
    | 'lowPF'
    | 'overload'
    | 'frequency'
    | 'highTHD'          // legacy — kept for type compat; no longer emitted by detectAnomalies
    | 'highVoltageTHD'   // voltage phases only (vR / vY / vB THD %)
    | 'highCurrentTHD'   // current phases only (iR / iY / iB THD %)
    | 'dropout';
  label:     string;
  severity:  RiskLevel;
  value:     number;
  threshold: number;
}

export function detectAnomalies(
  row: TelemetryRow,
  thresholds = DEFAULT_THRESHOLDS,
): AnomalyHit[] {
  const hits: AnomalyHit[] = [];

  // Skip sensor dropout rows entirely — not real anomalies
  if (row.isDropout) {
    hits.push({
      kind: 'dropout', label: 'Sensor Dropout',
      severity: 'WARNING', value: 0, threshold: 0,
    });
    return hits;
  }

  // Neutral current (PDF §2.3 N thresholds)
  const nc = safe(row.neutralCurrentA);
  if (nc > thresholds.neutralCurrentA) {
    const sev: RiskLevel =
      nc >= 50 ? 'CRITICAL' : nc >= 30 ? 'HIGH' : 'WARNING';
    hits.push({ kind: 'neutralCurrent', label: 'Neutral Current', severity: sev, value: nc, threshold: thresholds.neutralCurrentA });
  }

  // Voltage unbalance (PDF §2.3 L)
  const vu = phaseImbalancePct(row);
  if (vu > thresholds.voltageUnbalancePct) {
    hits.push({
      kind: 'voltageUnbalance', label: 'Voltage Imbalance',
      severity: vu > 2 ? 'HIGH' : 'WARNING',
      value: vu, threshold: thresholds.voltageUnbalancePct,
    });
  }

  // Current unbalance (PDF §2.3 M)
  const cu = safe(row.currentUnbalancePct);
  if (cu > thresholds.currentUnbalancePct) {
    hits.push({
      kind: 'currentUnbalance', label: 'Current Imbalance',
      severity: cu > 50 ? 'CRITICAL' : cu > 30 ? 'HIGH' : 'WARNING',
      value: cu, threshold: thresholds.currentUnbalancePct,
    });
  }

  // Low PF
  if (isValidPfReading(row)) {
    const pf = powerFactor(row);
    if (pf > 0 && pf < thresholds.pfMin) {
      hits.push({
        kind: 'lowPF', label: 'Low Power Factor',
        severity: pf < 0.85 ? 'HIGH' : 'WARNING',
        value: pf, threshold: thresholds.pfMin,
      });
    }
  }

  // Overload
  if (safe(row.loadPct) > thresholds.loadPctMax) {
    hits.push({
      kind: 'overload', label: 'Approaching Overload',
      severity: row.loadPct > 100 ? 'CRITICAL' : 'WARNING',
      value: row.loadPct, threshold: thresholds.loadPctMax,
    });
  }

  // Frequency deviation (PDF: >0.5 Hz → WARNING, >1 Hz → HIGH)
  const freqDev = Math.abs(safe(row.frequencyHz, 50) - 50);
  if (freqDev > thresholds.frequencyDeviationHz) {
    hits.push({
      kind: 'frequency', label: 'Frequency Deviation',
      severity: freqDev > 1 ? 'HIGH' : 'WARNING',
      value: row.frequencyHz, threshold: 50,
    });
  }

  // High Voltage THD — voltage phases only (vR / vY / vB THD %).
  // Threshold: >5% WARNING, >15% HIGH (IEEE 519: 8% planning level for HT).
  // Only fires when voltage THD data is present (non-zero on at least one phase).
  const maxVTHD = Math.max(safe(row.vRThdPct), safe(row.vYThdPct), safe(row.vBThdPct));
  if (maxVTHD > 5) {
    hits.push({
      kind: 'highVoltageTHD', label: 'High Voltage THD',
      severity: maxVTHD > 15 ? 'HIGH' : 'WARNING',
      value: maxVTHD, threshold: 5,
    });
  }

  // High Current THD — current phases only (iR / iY / iB THD %).
  // Threshold: >10% WARNING, >20% HIGH (higher allowed for current per IEEE 519).
  // Only fires when current THD data is present (non-zero on at least one phase).
  const maxITHD = Math.max(safe(row.iRThdPct), safe(row.iYThdPct), safe(row.iBThdPct));
  if (maxITHD > 10) {
    hits.push({
      kind: 'highCurrentTHD', label: 'High Current THD',
      severity: maxITHD > 20 ? 'HIGH' : 'WARNING',
      value: maxITHD, threshold: 10,
    });
  }

  return hits;
}

/**
 * Anomaly density: % of eligible rows that breach at least one threshold.
 * Rows flagged as dropouts are excluded from the eligible count.
 */
export function anomalyPercent(rows: TelemetryRow[]): number {
  const eligible = rows.filter((r) => !r.isDropout);
  if (!eligible.length) return 0;
  return (eligible.filter((r) => detectAnomalies(r).length > 0).length / eligible.length) * 100;
}

/* -------------------------------------------------------------------------- */
/*  Tariff helpers                                                            */
/* -------------------------------------------------------------------------- */

export function todMix(rows: TelemetryRow[]): Record<ToDPeriod, number> {
  const acc: Record<ToDPeriod, number> = { 'OFF-PEAK': 0, NORMAL: 0, PEAK: 0 };
  for (const r of rows) acc[r.todPeriod] += 1;
  return acc;
}

/* -------------------------------------------------------------------------- */
/*  15-minute Max Demand (shared by billing.ts and PowerAnalysis.tsx)         */
/* -------------------------------------------------------------------------- */

export const MD_BLOCK_MS = 15 * 60 * 1000; // 15 minutes in ms

/**
 * Instantaneous apparent power (VA) for a single telemetry row.
 *
 * Per spec §1: VA = Watts_Total / PF_Avg.
 * Falls back to vaTotal when PF is invalid (zero, negative, or > 1).
 * All intermediate values computed to full floating-point precision;
 * callers round to 4 d.p. before display.
 */
export function instantVA(row: TelemetryRow): number {
  const watts = safe(row.wattsTotal);
  const pf    = Math.abs(safe(row.pfAvg));
  // Use Watts / PF_Avg per spec §1; fall back to meter vaTotal if PF is invalid
  if (watts > 0 && pf > 0 && pf <= 1.0001) return watts / Math.min(pf, 1);
  return safe(row.vaTotal);
}

/**
 * Splits the telemetry window into clock-aligned 15-minute demand blocks
 * and returns the time-weighted average kVA for each block.
 *
 * Algorithm (per spec §2):
 *   • For each block, collect all row readings that fall inside it.
 *   • Compute instantaneous VA = Watts_Total / PF_Avg per row (spec §1).
 *   • Apply trapezoidal integration over the actual timestamps within the block:
 *       kVAh = Σ [(VA_i + VA_{i+1}) / 2 × Δt_i]   (Δt in hours)
 *     then  avg kVA = kVAh / (total time spanned in hours)
 *
 *   For the ideal case of exactly 4 readings at t=0, 5, 10, 15 min this
 *   reduces exactly to the spec formula:
 *       avg kVA = [ (VA₀+VA₁)/2×5 + (VA₁+VA₂)/2×5 + (VA₂+VA₃)/2×5 ] / 15
 *              = (VA₀/2 + VA₁ + VA₂ + VA₃/2) × (5/15)
 *
 *   Irregular poll intervals (seconds offset between nominal 5-min marks)
 *   are handled correctly because Δt is computed from actual timestamps in ms.
 *
 * Inter-sample gaps > 30 min are skipped to avoid bridging outage holes.
 * Blocks with < 2 readings (not enough to form a trapezoid) are excluded.
 *
 * Precision: all intermediate sums kept at full float precision;
 * the returned kva is rounded to 4 decimal places.
 */
export function fifteenMinDemandBlocks(rows: TelemetryRow[]): { ts: number; kva: number }[] {
  if (rows.length < 2) return [];
  const sorted = [...rows].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  // Bucket each row into its clock-aligned 15-min block
  const buckets = new Map<number, TelemetryRow[]>();
  for (const r of sorted) {
    const blockTs = Math.floor(r.timestamp.getTime() / MD_BLOCK_MS) * MD_BLOCK_MS;
    const list    = buckets.get(blockTs);
    if (list) list.push(r);
    else buckets.set(blockTs, [r]);
  }

  const results: { ts: number; kva: number }[] = [];

  for (const [blockTs, pts] of buckets) {
    if (pts.length < 2) continue; // need at least 2 points to form a trapezoid

    // Sort the bucket's rows by timestamp (they arrive in order but be safe)
    pts.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    // Trapezoidal integration within this 15-min block
    let kvahSum  = 0; // accumulates kVAh (VA × hours → kVAh via / 1000 at end)
    let spanMs   = 0; // total spanned time in ms (sum of all valid intervals)

    for (let i = 1; i < pts.length; i++) {
      const dtMs = pts[i].timestamp.getTime() - pts[i - 1].timestamp.getTime();
      // Skip gaps > 30 min — indicates an outage, not a real interval
      if (dtMs <= 0 || dtMs > 30 * 60 * 1000) continue;

      const dtH   = dtMs / 3_600_000;                    // interval in hours (full precision)
      const va0   = instantVA(pts[i - 1]);               // VA at left edge
      const va1   = instantVA(pts[i]);                   // VA at right edge
      // Trapezoidal area in VA·h; divide by 1000 to get kVAh
      kvahSum += ((va0 + va1) / 2) * dtH;
      spanMs  += dtMs;
    }

    if (spanMs <= 0) continue;

    const spanH  = spanMs / 3_600_000;                   // total span in hours
    const avgKva = (kvahSum / 1000) / spanH;             // average kVA = kVAh / hours

    // Round to 4 d.p. for storage; display layer truncates to 2 d.p.
    results.push({ ts: blockTs, kva: Math.round(avgKva * 10000) / 10000 });
  }

  return results.sort((a, b) => a.ts - b.ts);
}

/**
 * Billing-cycle Max Demand (kVA): the largest 15-min averaged demand block
 * in the given row window. This is the peak kVA for billing purposes.
 *
 * Spec §5: "the absolute highest maximum value achieved is officially
 * designated as the peak kVA for that day."
 *
 * Rounded to 4 d.p.; display layer truncates to 2 d.p.
 */
export function maxDemandKvaOf(rows: TelemetryRow[]): number {
  return fifteenMinDemandBlocks(rows).reduce((m, b) => (b.kva > m ? b.kva : m), 0);
}